import { useCallback, useMemo } from "react";

import { type CommandDefinition, unavailableReason } from "./commands";
import { useFleetSettings } from "./queries";
import type { Device } from "./types";
import { isOlder, type Requirements, versionReason } from "./versions";

export { isOlder, parseVersion, versionReason } from "./versions";

export interface AgentInfo {
  /** The newest agent release, once the server said. */
  latest: string | null;
  requirements: Requirements | undefined;
  /** Why a command can't be sent to the device right now, or null. */
  reasonFor(definition: CommandDefinition, device: Device): string | null;
  /** Why a feature that isn't a command (e.g. "network_details") isn't there, or null. */
  featureReason(feature: string, device: Device): string | null;
  /** Whether the device runs an agent older than the newest release. */
  isOutdated(device: Pick<Device, "agent_version">): boolean;
  /** What the device misses until its agent is updated, with the version each needs. */
  missing(device: Device): { feature: string; minVersion: string; reason: string }[];
}

export function useAgentInfo(): AgentInfo {
  const settings = useFleetSettings();
  const latest = settings.data?.latest_agent_version ?? null;
  const requirements = settings.data?.agent_requirements;

  const reasonFor = useCallback(
    (definition: CommandDefinition, device: Device) =>
      unavailableReason(definition, device, requirements),
    [requirements],
  );
  const featureReason = useCallback(
    (feature: string, device: Device) => versionReason(feature, device, requirements),
    [requirements],
  );
  const isOutdated = useCallback(
    (device: Pick<Device, "agent_version">) =>
      latest !== null && isOlder(device.agent_version, latest),
    [latest],
  );
  const missing = useCallback(
    (device: Device) =>
      Object.entries(requirements ?? {})
        .filter(([, requirement]) => isOlder(device.agent_version, requirement.min_version))
        .map(([feature, requirement]) => ({
          feature,
          minVersion: requirement.min_version,
          reason: requirement.reason,
        })),
    [requirements],
  );

  return useMemo(
    () => ({ latest, requirements, reasonFor, featureReason, isOutdated, missing }),
    [latest, requirements, reasonFor, featureReason, isOutdated, missing],
  );
}
